import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { useT } from "@/i18n/LanguageProvider";

export const FAQ = () => {
    const t = useT();

    const FAQS = [
        {
            q: t({
                de: "Wann beginnt der Bau und wann ist mit der Übergabe zu rechnen?",
                en: "When does construction start and when is handover expected?",
            }),
            a: t({
                de: "Die Baugenehmigung liegt vor. Konkrete Termine zu Baustart und voraussichtlicher Übergabe besprechen wir gerne im persönlichen Gespräch nach Eingang Ihrer Anfrage.",
                en: "The building permit has been granted. Specific dates for construction start and handover will be discussed in a personal conversation once we receive your inquiry.",
            }),
        },
        {
            q: t({
                de: "Was bedeutet „erweiterter Ausbauzustand“?",
                en: "What does “extended fit-out condition” mean?",
            }),
            a: t({
                de: "Die Wohnungen werden komplett bezugsfertig übergeben – inklusive gefliester Badezimmer und installierter Sanitär- und Heiztechnik. Bodenbeläge (Parkett/Vinyl) sowie Malerarbeiten/Tapeten sind nicht enthalten und können von Ihnen individuell ausgewählt und ausgeführt werden. So bestimmen Sie Optik und Material selbst.",
                en: "Apartments are handed over fully ready to move in – including tiled bathrooms and installed plumbing and heating. Flooring (parquet/vinyl) as well as painting/wallpaper are not included so that you can choose and carry these out yourself, controlling look and material.",
            }),
        },
        {
            q: t({
                de: "Wie funktioniert die KfW-Förderung im Standard Effizienzhaus 40?",
                en: "How does the KfW funding for energy class 40 work?",
            }),
            a: t({
                de: "Über das KfW-Programm „Klimafreundlicher Neubau“ sind zinsvergünstigte Darlehen mit attraktiven Konditionen möglich – sowohl für Eigennutzer als auch für Kapitalanleger. Die Beantragung erfolgt über Ihre durchleitende Hausbank vor Notarvertrag. Wir geben Ihnen alle Unterlagen mit, die Sie für den Antrag benötigen.",
                en: "Via the KfW programme “Climate-friendly New Build”, subsidised loans with attractive terms are available – for owner-occupiers and investors alike. Applications are made through your bank before notarisation. We provide all the documents you need for the application.",
            }),
        },
        {
            q: t({
                de: "Ist die Photovoltaik-Anlage inklusive?",
                en: "Is the photovoltaic system included?",
            }),
            a: t({
                de: "Die PV-Anlage ist inklusive: Jede Wohnung erhält eine 2 kW Photovoltaik-Anlage inkl. passendem Stromspeicher separat für jede Wohnung – im Kaufpreis enthalten. Größere Anlagen oder zusätzliche Speicher-Kapazität können auf Wunsch individuell konfiguriert werden.",
                en: "The PV system is included: every apartment comes with a 2 kW photovoltaic system and a dedicated matching battery storage – part of the purchase price. Larger systems or additional battery capacity can be configured individually on request.",
            }),
        },
        {
            q: t({
                de: "Welche Heizung ist eingebaut?",
                en: "What kind of heating is installed?",
            }),
            a: t({
                de: "Jede Wohnung erhält eine eigene Luft-Luft-Wärmepumpe. Vorteil: kein gemeinsamer Heizkessel, eigene Verbrauchszuordnung, niedrige Betriebskosten und keine Aufteilungs-Diskussionen in der Eigentümergemeinschaft.",
                en: "Each apartment has its own air-to-air heat pump. Benefit: no shared boiler, individual consumption tracking, low operating costs and no allocation debates within the owners' community.",
            }),
        },
        {
            q: t({
                de: "Gibt es eine Wallbox am Stellplatz?",
                en: "Is there an EV wallbox at the parking spot?",
            }),
            a: t({
                de: "An jedem Carport-Stellplatz ist der Anschluss für eine Wallbox vorbereitet (Leerrohre, Strom). Die Wallbox selbst können Sie nach Wunsch über uns oder einen Elektriker Ihres Vertrauens beziehen.",
                en: "Every carport spot has the connection for a wallbox prepared (conduits, power). The wallbox itself can be sourced through us or an electrician of your choice.",
            }),
        },
        {
            q: t({
                de: "Können wir die Wohnung vor dem Kauf besichtigen?",
                en: "Can we view the apartment before purchase?",
            }),
            a: t({
                de: "Da es sich um ein Neubauvorhaben handelt, ist eine Besichtigung der fertigen Wohnung erst nach Bauabschluss möglich. Vor Notarvertrag bieten wir gerne einen Termin am Bauplatz sowie ein ausführliches Gespräch zu den Plänen an.",
                en: "Since this is a new-build project, viewing the finished apartment is only possible after construction. Before notarisation we are happy to arrange an on-site visit and a detailed walk-through of the plans.",
            }),
        },
        {
            q: t({
                de: "Sind Reservierungen möglich?",
                en: "Can apartments be reserved?",
            }),
            a: t({
                de: "Ja. Nach einem persönlichen Gespräch und der Klärung der Finanzierungsgrundlage reservieren wir Ihre Wunsch-Wohnung schriftlich für einen vereinbarten Zeitraum, in dem Sie die Notar-Beurkundung vorbereiten können.",
                en: "Yes. After a personal conversation and once the financing basis is clarified, we reserve your apartment of choice in writing for an agreed period during which you can prepare the notarial deed.",
            }),
        },
        {
            q: t({
                de: "Kann ich Sonderwünsche einbringen?",
                en: "Can I add special requests?",
            }),
            a: t({
                de: "In einem gewissen Rahmen ja – z. B. Kaminanschluss (im Penthouse vorgesehen), Bemusterung der Bäder, PV-Konfiguration, Wallbox-Variante. Wir sprechen das individuell und transparent mit Festpreis-Aufschlägen ab.",
                en: "Within reason, yes – e.g. fireplace connection (planned for the penthouse), bathroom finishes, PV configuration, wallbox variant. We discuss these individually and transparently with fixed-price surcharges.",
            }),
        },
    ];

    return (
        <section
            id="faq"
            className="bg-white py-24 sm:py-32 border-y border-black/5"
            data-testid="faq-section"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                    <div className="lg:col-span-6">
                        <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-500">
                            {t({ de: "Häufige Fragen", en: "Frequently asked" })}
                        </span>
                        <h2 className="font-display font-black tracking-tight text-sage-500 text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-balance">
                            {t({ de: "Antworten auf das,", en: "Answers to" })}
                            <span className="text-terracotta-400">
                                {t({ de: " was Käufer fragen.", en: " what buyers ask." })}
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <p className="font-body text-lg text-sage-500/75 leading-relaxed">
                            {t({
                                de: "Wenn Ihre Frage nicht dabei ist, schreiben Sie uns einfach – wir antworten schnell und persönlich.",
                                en: "If your question isn't covered here, just drop us a line – we respond quickly and personally.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="max-w-4xl">
                    <Accordion type="single" collapsible className="bg-linen border border-black/5 rounded-3xl overflow-hidden">
                        {FAQS.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`faq-${i}`}
                                className="border-b border-black/5 last:border-b-0"
                                data-testid={`faq-item-${i}`}
                            >
                                <AccordionTrigger className="px-6 py-5 text-left font-display font-bold text-base sm:text-lg text-sage-500 hover:bg-mint-50 hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-5 font-body text-base text-sage-500/80 leading-relaxed">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
};
